import React, { useRef, useCallback } from 'react'
import { css } from '@emotion/core'
import { formatValue } from '@fs/zion-locale'
import { easeOutSine } from 'js-easing-functions'
import Countup from 'react-countup'
import { Player as Lottie } from '@lottiefiles/react-lottie-player'
import { number, func } from 'prop-types'

import lottieConfetti from '../../assets/lottieFiles/confetti.json'

import Ribbon from '../Ribbon/Ribbon'
import coinLottie from '../../assets/lottieFiles/jump-spin-coin.json'
import useDoubleRAF from '../../hooks/useDoubleRAF'

export default function PointsAnimator({ startPoints, duration, endPoints, animationCompleteHandler }) {
  const pointsWrapperCss = css`
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    margin-top: -75px;
    width: 100%;
  `
  const lottieCoinRef = useRef()
  const lottieConfettiRef = useRef()
  const mountTimeRef = useRef(Date.now())
  const hasFinishedRef = useRef(false)

  useDoubleRAF(() => {
    lottieCoinRef.current?.play()
    lottieConfettiRef.current?.play()
    if (lottieConfettiRef.current?.container) {
      lottieConfettiRef.current.container.style.display = 'block'
    }
  }, [])

  const finishHandler = useCallback(() => {
    if (hasFinishedRef.current) return
    hasFinishedRef.current = true

    if (lottieConfettiRef.current?.container) {
      lottieConfettiRef.current.container.style.display = 'none'
    }
    animationCompleteHandler()
  }, [animationCompleteHandler])

  const handleCountupEnd = useCallback(() => {
    const elapsedTime = Date.now() - mountTimeRef.current
    const minAnimationTime = duration * 1000

    if (elapsedTime >= minAnimationTime) {
      finishHandler()
    } else {
      // Wait for the remaining time before finishing
      const remainingTime = minAnimationTime - elapsedTime
      setTimeout(finishHandler, remainingTime)
    }
  }, [duration, finishHandler])

  return (
    <div css={pointsWrapperCss}>
      <Lottie
        style={{
          width: '100%',
          maxWidth: '1200px',
          display: 'none',
          position: 'absolute',
          insetInlineEnd: 0,
          insetInlineStart: 0,
        }}
        ref={lottieConfettiRef}
        src={lottieConfetti}
        loop={false}
        autoplay={false}
        keepLastFrame
      />

      <Lottie
        src={coinLottie}
        ref={lottieCoinRef}
        style={{ width: '300px', position: 'relative', top: 80 }}
        loop={false}
        autoplay={false}
      />
      <Ribbon>
        <Countup
          start={startPoints}
          end={endPoints}
          preserveValue
          duration={duration}
          formattingFn={formatValue}
          useEasing
          onEnd={handleCountupEnd}
          easingFn={easeOutSine}
        />
      </Ribbon>
    </div>
  )
}

PointsAnimator.propTypes = {
  startPoints: number.isRequired,
  duration: number.isRequired,
  endPoints: number.isRequired,
  animationCompleteHandler: func.isRequired,
}
